import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from '../lib/safeToast';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { getApiErrorMessage } from '../lib/utils';
import { Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const { login, verify2FA } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.success) {
        toast.success('Welcome back!');
        navigate(result.user?.role === 'admin' ? '/backoffice/dashboard' : '/dashboard');
      } else if (result.requires2FA) {
        setShow2FA(true);
        toast.info('Please enter your 2FA code');
      }
    } catch (error) {
      const msg = getApiErrorMessage(error.response?.data?.detail, 'Login failed');
      toast.error(String(msg ?? 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    const code = (twoFACode || '').replace(/\D/g, '').slice(0, 6);
    if (!code || code.length < 6) {
      toast.error('Please enter all 6 digits');
      return;
    }
    setLoading(true);
    try {
      const result = await verify2FA(code);
      toast.success('Welcome back!');
      navigate(result?.user?.role === 'admin' ? '/backoffice/dashboard' : '/dashboard');
    } catch (error) {
      const msg = getApiErrorMessage(error.response?.data?.detail, 'Invalid 2FA code');
      toast.error(String(msg ?? 'Invalid 2FA code'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <img src="/logo.png" alt="PayZen" className="w-12 h-12 rounded-xl object-contain" />
            <div>
              <h1 className="font-bold text-xl  text-[#1C472B]">PayZen</h1>
              <p className="text-xs text-slate-500">Merchant Hub</p>
            </div>
          </div>

          {!show2FA ? (
            <>
              <h2 className="text-3xl font-extrabold text-[#0F172A]  mb-2">
                Welcome back
              </h2>
              <p className="text-slate-500 mb-8">
                Sign in to access your merchant dashboard
              </p>

              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="input-label">Email Address</label>
                  <Input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="input-field"
                    required
                    data-testid="login-email"
                  />
                </div>

                <div>
                  <label className="input-label">Password</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="input-field pr-12"
                      required
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="mt-2 text-right">
                    <Link to="/forgot-password" className="text-sm text-[#1C472B] font-medium hover:underline" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base rounded-full font-semibold bg-[#1C472B] hover:bg-[#143620] active:bg-[#0F2818] text-white shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              <p className="text-center mt-8 text-slate-500">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#1C472B] font-semibold hover:underline">
                  Create one
                </Link>
              </p>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-extrabold text-[#0F172A]  mb-2">
                Two-Factor Authentication
              </h2>
              <p className="text-slate-500 mb-8">
                Enter the code from your authenticator app
              </p>

              <form onSubmit={handle2FAVerify} className="space-y-6">
                <div>
                  <label className="input-label">Authentication Code</label>
                  <Input
                    type="text"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="input-field text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                    required
                    data-testid="2fa-code"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base rounded-full font-semibold bg-[#1C472B] hover:bg-[#143620] active:bg-[#0F2818] text-white shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  disabled={loading || twoFACode.length !== 6}
                  data-testid="2fa-submit"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Verify'
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setShow2FA(false);
                    setTwoFACode('');
                  }}
                  className="w-full text-center text-slate-500 hover:text-[#1C472B]"
                >
                  Back to login
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Right Panel - Image */}
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1666618207644-4de0226a3f85?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      >
        <div className="h-full w-full bg-[#1C472B]/80 flex items-center justify-center p-12">
          <div className="text-center text-white max-w-lg">
            <h2 className="text-4xl font-extrabold  mb-4">
              Secure Banking for Modern Merchants
            </h2>
            <p className="text-lg text-slate-300">
              Manage payments, track transactions, and grow your business with PayZen merchant dashboard.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
